import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {PolicyEditActions} from './policy-edit.actions';
import {instanceToInstance} from 'class-transformer';
import isEqual from 'lodash-es/isEqual';
import {ValidationStateModel} from '../../modules/validation/models/validation-state.model';
import {GeneralContract, Policy} from '../../models/api/product/base.model';
import {Person} from '../../models/api/person.model';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {PersonalDataFormComponent} from '../../modules/shared/components/personal-data-form/personal-data-form.component';
import {PolicyEdit} from '../../modules/registration/components/policy-edit/model/policy-edit.model';
import {PersonService} from '../../services/person.service';
import {NotificationService} from '../../services/notification.service';
import {GeneralContractOCZ, PolicyOCZ} from '../../models/api/product/ocz.model';
import {ProductTypeEnum} from '../../enums/product-type.enum';
import {clearProperties, deepPick, isPrimitive, matches} from '../../helpers/object.helper';
import {RiskOCZ} from '../../models/api/variant/risk/risk-ocz.model';
import {catchError, tap} from 'rxjs/operators';
import {ValidationActions} from '../../modules/validation/state/validation.actions';
import {HttpErrorResponse} from '@angular/common/http';
import {of} from 'rxjs';
import {NavService} from '../../services/nav.service';
import {PolicyService} from '../../services/policy.service';
import {PolicyNNWKL} from '../../models/api/product/nnwkl.model';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {StatusChange} from '../../models/api/status-change.model';
import {SearchListActions} from '../search-list/search-list.actions';
import {DictionaryItem} from '../../models/api/dictionary.model';
import {Installment} from '../../models/api/variant/risk/installment.model';
import {patch} from '@ngxs/store/operators';
import {skipInterceptors} from '../../helpers/request.helper';
import {SubjectEditModalComponent} from '../../modules/shared/components/subject-edit-modal/subject-edit-modal.component';
import InsuredEvent = PolicyEdit.InsuredEvent;
import InsuredEventType = PolicyEdit.InsuredEventType;
import {PolicyEditViewContext} from '../../modules/registration/components/policy-edit/policy-edit.const';

interface PolicyEditStateModel extends ValidationStateModel {
    originalPolicy?: Policy;
    generalContract?: GeneralContract;
    activePolicy?: Policy;
    insuredEvent?: InsuredEvent;
    changes?: PolicyChanges;
    premium?: number;
    installments?: Installment[];
    validationMessage?: string;
}

export interface PolicyChanges {
    insurer?: PolicyChangesTypes;
    insureds?: PolicyChangesTypes;
    professionGroup?: PolicyChangesTypes;
    variants?: PolicyChangesTypes;
    selectedInstallment?: PolicyChangesTypes;
}

export enum PolicyChangesTypes {
    MODIFIED = 'MODIFIED',
    DELETED = 'DELETED',
    RISKS_MODIFIED = 'RISKS_MODIFIED',
}

@State<PolicyEditStateModel>({
    name: PolicyEditState.STATE_NAME,
    defaults: {
        generalContract: null,
        originalPolicy: null,
        validationErrors: null,
        activePolicy: null,
        insuredEvent: null,
        changes: null,
        premium: null,
        installments: null,
        validationMessage: null,
    },
})
@Injectable()
export class PolicyEditState {
    static readonly STATE_NAME = 'policyEdit';

    constructor(
        protected modalService: ModalService,
        protected translate: TranslateService,
        protected personService: PersonService,
        protected notificationService: NotificationService,
        protected navService: NavService,
        protected policyService: PolicyService
    ) {}

    @Selector()
    static generalContract(state: PolicyEditStateModel) {
        return state.generalContract;
    }

    @Selector()
    static originalPolicy(state: PolicyEditStateModel) {
        return state.originalPolicy;
    }

    @Selector()
    static activePolicy(state: PolicyEditStateModel) {
        return state.activePolicy;
    }

    @Selector()
    static insuredEvent(state: PolicyEditStateModel) {
        return state.insuredEvent;
    }

    @Selector()
    static premium(state: PolicyEditStateModel) {
        return state.premium;
    }

    @Selector()
    static installments(state: PolicyEditStateModel) {
        return state.installments;
    }

    @Selector()
    static changes(state: PolicyEditStateModel) {
        return state.changes;
    }

    @Selector()
    static validationMessage(state: PolicyEditStateModel) {
        return state.validationMessage;
    }

    @Action(PolicyEditActions.SetPolicyData)
    setPolicyData(
        ctx: StateContext<PolicyEditStateModel>,
        {generalContract, viewContext, originalPolicy}: PolicyEditActions.SetPolicyData
    ) {
        const activePolicy = this.mapPolicyData(generalContract, originalPolicy);
        let changes: PolicyChanges = null;
        if (originalPolicy) {
            changes = this.checkChanges(activePolicy, generalContract);
        }

        if (viewContext === PolicyEditViewContext.COPY_DRAFT) {
            this.clearCopiedData(activePolicy);
        }
        ctx.patchState({
            generalContract,
            originalPolicy,
            activePolicy,
            changes,
            premium: activePolicy?.premium ?? null,
        });
    }

    @Action(PolicyEditActions.EditInsured)
    editInsured(ctx: StateContext<PolicyEditStateModel>, {index, personData, errors}: PolicyEditActions.EditInsured) {
        return this.modalService.generic({
            nzTitle: this.translate.instant(personData ? 'POLICY.EDIT_INSURED' : 'POLICY.ADD_INSURED'),
            nzContent: SubjectEditModalComponent,
            nzData: {
                personData,
                errors,
                insuredMode: ctx.getState().generalContract?.insuredMode?.code,
            },
            nzOkText: this.translate.instant(personData ? 'LABELS.EDIT' : 'LABELS.ADD'),
            nzWidth: '70%',
            nzOnOk: (instance: SubjectEditModalComponent) => {
                ctx.patchState({
                    insuredEvent: {
                        type: personData ? InsuredEventType.EDIT : InsuredEventType.ADD,
                        personData: instance.formData,
                        index,
                    },
                });
            },
        });
    }

    @Action(PolicyEditActions.DeleteInsured)
    deleteInsured(ctx: StateContext<PolicyEditStateModel>, {index}: PolicyEditActions.DeleteInsured) {
        return this.modalService.generic({
            nzTitle: this.translate.instant('POLICY.DELETE_INSURED'),
            nzContent: this.translate.instant('POLICY.DELETE_INSURED_PROMPT'),
            nzOnOk: (instance: PersonalDataFormComponent) => {
                ctx.patchState({
                    insuredEvent: {
                        type: InsuredEventType.DELETE,
                        index,
                    },
                });
            },
        });
    }

    @Action(PolicyEditActions.PersonImport)
    personImport(ctx: StateContext<PolicyEditStateModel>, {file}: PolicyEditActions.PersonImport) {
        return this.personService.getPersonsFromFile(file).subscribe({
            next: (res) => {
                ctx.patchState({
                    insuredEvent: {
                        type: InsuredEventType.IMPORT,
                        persons: res,
                    },
                });
            },
            error: () => {
                this.notificationService.error('POLICY.IMPORT_ERROR');
            },
        });
    }

    @Action(PolicyEditActions.Calculate)
    calculate(ctx: StateContext<PolicyEditStateModel>, {body, productCode}: PolicyEditActions.Calculate) {
        return this.policyService.calculate(body, productCode, skipInterceptors([409, 404])).pipe(
            tap((res) => {
                ctx.patchState({premium: res.premium, installments: res.installments, validationMessage: null});
            }),
            catchError((rej: HttpErrorResponse) => {
                ctx.patchState({premium: null, installments: null, validationMessage: this.getManualValidation(rej)});
                return of(null);
            })
        );
    }

    @Action(PolicyEditActions.Save)
    save(ctx: StateContext<PolicyEditStateModel>, {body, productCode, context}: PolicyEditActions.Save) {
        const updating = context === PolicyEditViewContext.EDIT_DRAFT ? true : false;
        const headers = skipInterceptors([409, 404]);
        const req$ = updating
            ? this.policyService.updatePolicy(body, productCode, headers)
            : this.policyService.savePolicy(body, productCode, headers);
        return req$.pipe(
            tap((res: Policy) => {
                this.notificationService.success(updating ? 'POLICY.UPDATED' : 'POLICY.CREATED');
                this.navService.navigateByCode(NavItemCodes.POLICY_PREVIEW, res?.id);
            }),
            catchError((rej: HttpErrorResponse) => {
                ctx.patchState({validationMessage: this.getManualValidation(rej)});
                ctx.dispatch(new ValidationActions.Set(PolicyEditState.STATE_NAME, rej.error));
                return of(null);
            })
        );
    }

    @Action(PolicyEditActions.Clear)
    clear(ctx: StateContext<PolicyEditStateModel>) {
        ctx.setState({
            insuredEvent: null,
            changes: null,
            validationErrors: null,
            installments: null,
            premium: null,
            validationMessage: null,
        });
    }

    @Action(PolicyEditActions.AcceptPoolPolicy)
    acceptPoolPolicy(
        ctx: StateContext<PolicyEditStateModel>,
        {policyId, refreshListCode}: PolicyEditActions.AcceptPoolPolicy
    ) {
        return this.modalService.confirm({
            nzContent: this.translate.instant('POLICY.ACCEPT_APPLICATION_CONFIRMATION'),
            nzTitle: this.translate.instant('POLICY.ACCEPT_APPLICATION_MODAL_TITLE'),
            nzOnOk: async () => {
                try {
                    await this.changeStatus(policyId, 'ACTIVE').toPromise();
                    this.notificationService.success('POLICY.POLICY_STATUS_CHANGE_SUCCESS');
                    ctx.dispatch(new SearchListActions.GetListPage(refreshListCode));
                    return true;
                } catch (e) {
                    const reject: HttpErrorResponse = e;
                    this.notificationService.error(reject?.error?.message);
                    return false;
                }
            },
        });
    }

    @Action(PolicyEditActions.ClearCalculationData)
    clearCalculationData(ctx: StateContext<PolicyEditStateModel>) {
        ctx.patchState({
            installments: null,
            premium: null,
        });
    }

    @Action(PolicyEditActions.PatchChanges)
    patchChanges(ctx: StateContext<PolicyEditStateModel>, {changes}: PolicyEditActions.PatchChanges) {
        ctx.setState(
            patch({
                changes: patch(changes),
            })
        );
    }

    protected clearCopiedData(copy: Policy) {
        clearProperties(copy, ['id', 'number', 'hash', 'status', 'exportStatus', 'exportStatusDate', 'statusDate']);
    }

    protected changeStatus(policyId: number, status: string) {
        const statusItem = new StatusChange({newStatus: new DictionaryItem({code: status})});
        return this.policyService.changeStatus(policyId, statusItem);
    }

    protected mapPolicyData(generalContract: GeneralContract, originalPolicy: Policy): Policy {
        if (originalPolicy) {
            const policyCopy: Policy = instanceToInstance(originalPolicy, {ignoreDecorators: true});
            for (const risk of policyCopy.variant.risks) {
                risk.selected = true;
            }
            // // set default dates when editing
            // policyCopy.start = null;
            // policyCopy.conclusion = null;
            // policyCopy.stop = null;
            return policyCopy;
        } else {
            return this.mapNewPolicyData(generalContract);
        }
    }

    protected mapNewPolicyData(generalContract: GeneralContract): Policy {
        const policy = this.getPolicyModel(generalContract?.product?.code);
        policy.generalContract = instanceToInstance(generalContract, {ignoreDecorators: true});
        if (policy.generalContract?.insurer) {
            policy.insurer = policy.generalContract.insurer;
        } else if (!policy.insurer) {
            policy.insurer = new Person();
        }
        policy.variant = instanceToInstance(generalContract.variants?.[0], {
            ignoreDecorators: true,
        });
        return policy;
    }

    protected getPolicyModel(productCode: ProductTypeEnum | string): Policy {
        switch (productCode) {
            case ProductTypeEnum.OCZ:
                return new PolicyOCZ();
            case ProductTypeEnum.NNWKL:
                return new PolicyNNWKL();
            default:
                return new Policy();
        }
    }

    protected checkChanges(policy: Policy, generalContract: GeneralContract): PolicyChanges {
        const changes: PolicyChanges = {
            insurer: null,
            insureds: null,
            professionGroup: null,
            variants: null,
        };
        if (!isEqual(policy.generalContract, generalContract)) {
            policy.premium = null;
            changes.insurer = this.checkInsurerChanges(policy, generalContract);
            changes.insureds = this.checkInsuredChanges(policy, generalContract);
            if (generalContract?.product?.code === ProductTypeEnum.OCZ) {
                changes.professionGroup = this.checkProfessionGroupChanges(
                    policy as PolicyOCZ,
                    generalContract as GeneralContractOCZ
                );
                if (!changes.professionGroup) {
                    changes.professionGroup = this.checkOCZRisks(
                        policy as PolicyOCZ,
                        generalContract as GeneralContractOCZ
                    );
                }
            } else {
                changes.variants = this.checkNNWPolicy(policy, generalContract);
            }
        }
        return changes;
    }

    protected checkInsurerChanges(policy?: Policy, gc?: GeneralContract): PolicyChangesTypes | null {
        const insurerEqual = this.primitivesEqual(policy?.generalContract?.insurer, gc?.insurer);
        if (insurerEqual) {
            return null;
        } else if ((!policy?.generalContract?.insurer && gc?.insurer) || !insurerEqual) {
            policy.insurer = gc?.insurer;
            return PolicyChangesTypes.MODIFIED;
        } else if (policy?.generalContract?.insurer && !gc.insurer) {
            policy.insurer = new Person();
            return PolicyChangesTypes.MODIFIED;
        } else {
            return null;
        }
    }

    protected checkInsuredChanges(policy?: Policy, gc?: GeneralContract): PolicyChangesTypes | null {
        const policyCode = policy.generalContract?.insuredMode?.code;
        const gcCode = gc.insuredMode.code;
        if (policyCode === gcCode) {
            return null;
        } else if (policyCode === 'FULL' && gcCode === 'PESEL') {
            for (const insured of policy.insureds) {
                insured.street = null;
                insured.flatNumber = null;
                insured.houseNumber = null;
                insured.city = null;
                insured.nip = null;
                insured.postCode = null;
            }
            return PolicyChangesTypes.MODIFIED;
        } else if (
            (policyCode === 'PESEL' && gcCode === 'FULL') ||
            (policyCode === 'ANONYMOUS' && ['FULL', 'PESEL'].includes(gcCode))
        ) {
            policy.insureds = [];
            return PolicyChangesTypes.DELETED;
        } else {
            return null;
        }
    }

    protected checkProfessionGroupChanges(policy?: PolicyOCZ, gc?: GeneralContractOCZ): PolicyChangesTypes | null {
        if (isEqual(policy?.generalContract?.professionGroups, gc.professionGroups)) {
            return null;
        }
        const gcFoundGroup = gc?.professionGroups?.find((gcGroup) => gcGroup?.hash === policy?.professionGroup?.hash);
        if (gcFoundGroup && isEqual(gcFoundGroup, policy?.professionGroup)) {
            return null;
        } else {
            policy.professionGroup = gc?.professionGroups?.[0];
            this.resetRisks(policy);
            return gcFoundGroup ? PolicyChangesTypes.MODIFIED : PolicyChangesTypes.DELETED;
        }
    }

    protected checkOCZRisks(policy?: PolicyOCZ, gc?: GeneralContractOCZ): PolicyChangesTypes | null {
        const fieldsToCheck: Array<keyof RiskOCZ> = [
            'obligatory',
            'integralFranchiseDomain',
            'reductionFranchiseDomain',
            'availableInsuranceSums',
            'tarificationMatrix',
            'paidExtensions',
        ];
        let changes: PolicyChangesTypes | null = null;
        for (const policyRisk of policy?.generalContract?.variants?.[0]?.risks) {
            const gcRisk = gc?.variants?.[0]?.risks?.find((gcItem) => gcItem.code === policyRisk.code);
            if (!gcRisk || !matches(gcRisk, deepPick(policyRisk, fieldsToCheck))) {
                changes = PolicyChangesTypes.RISKS_MODIFIED;
                break;
            }
        }
        if (policy?.generalContract?.variants?.[0]?.risks?.length !== gc?.variants?.[0]?.risks?.length) {
            changes = PolicyChangesTypes.RISKS_MODIFIED;
        }
        if (changes) {
            this.resetRisks(policy);
        }
        return changes;
    }

    protected checkNNWPolicy(policy: Policy, gc: GeneralContract): PolicyChangesTypes | null {
        const variantName = policy?.variant?.name;
        const policyVariant = policy?.generalContract?.variants?.find((variant) => variant?.name === variantName);
        const gcVariant = gc?.variants?.find((variant) => variant?.name === variantName);
        if (!policyVariant || !gcVariant) {
            return PolicyChangesTypes.DELETED;
        } else {
            return isEqual(policyVariant, gcVariant) ? null : PolicyChangesTypes.MODIFIED;
        }
    }

    protected resetRisks(policy: PolicyOCZ) {
        for (const risk of policy?.variant?.risks) {
            risk.selected = false;
            risk.insuranceSum = null;
            risk.reductionFranchise = null;
            risk.integralFranchise = null;
        }
        policy.paidExtensions = null;
    }

    protected primitivesEqual(obj1: object, obj2: object) {
        if ((obj1 && !obj2) || (!obj1 && obj2)) {
            return false;
        }
        for (const key in obj1) {
            if (obj1.hasOwnProperty(key) && isPrimitive(obj1[key])) {
                if (obj1[key] !== obj2[key]) {
                    return false;
                }
            }
        }
        return true;
    }

    protected getManualValidation(reject?: HttpErrorResponse): string | null {
        switch (reject?.status) {
            case 409:
                return reject.error?.[0]?.defaultMessage ?? 'ERRORS.UNKNOWN_ERROR';
            case 404:
                return 'ERRORS.UNKNOWN_ERROR';
            default:
                return null;
        }
    }
}
